import type { Placement } from '@popperjs/core';
import type { ButtonPriority, ButtonSize } from '@timelyapp/react';
import { ButtonGroup, Checkbox, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import UpgradeState from 'components/account-features/UpgradeState';
import ClearableButton from 'components/buttons/ClearableButton';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import styles from 'components/dropdowns/TabbedUserFilterDropdown.module.scss';
import FilterList from 'components/FilterList';
import Popover from 'components/popovers/Popover';
import type { TeamFilterDropdownOption } from 'factories/teams';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Avatar } from 'types/user';
import type { FilterListItem } from 'utils/filter-list';
import { countableLabel } from 'utils/text-helper';

interface OptionMeta {
  isInFocus: boolean;
  isSelected: boolean;
  isDisabled?: boolean;
  hint?: string;
  hintTooltip?: React.ReactNode;
}

const tabs = [
  {
    value: 'users',
    label: 'Users',
  },
  {
    value: 'teams',
    label: 'Teams',
  },
] as const;

interface UserOptionFactoryInput {
  id: number;
  name: string;
  avatar?: Avatar | string | null;
  avatar_url?: string;
  deleted?: boolean;
}
const userOptionFactory = (user: UserOptionFactoryInput) => {
  const avatar = user.avatar
    ? typeof user.avatar === 'string'
      ? user.avatar
      : user.avatar.medium_retina
    : user.avatar_url;
  const image = avatar;

  return {
    value: user.id,
    name: user.name,
    image,
    imageIsAvatar: user.id >= 0,
    groupFilter: user.deleted ? 'deleted' : 'active',
  };
};

type Tab = 'users' | 'teams';

export interface Props {
  users: Array<UserOptionFactoryInput>;
  selectedTeams: Array<number>;
  selectedUsers: Array<number>;
  compact?: boolean;
  emptyLabel?: string;
  position?: Placement;
  teams: Array<TeamFilterDropdownOption>;
  unrestrictedWhenEmpty?: boolean;
  singleSelectTeam?: boolean;
  singleSelectUser?: boolean;
  buttonFormType?: string;
  label?: string | null | undefined | React.ReactNode;
  buttonPriority?: ButtonPriority | null | undefined;
  buttonClassName?: string;
  faIcon: string | null | undefined;
  onChange: (activeTab: Tab, ids: Array<number>) => void;
  fixed?: boolean;
  maxHeight?: number | string;
  minHeight?: number | string;
  preventOverflow?: boolean;
  allowFilterGroups: boolean;
  showUpgradeSubscription: boolean;
  onSearchUsers?: (searchString: string) => void;
  disabled?: boolean;
  clearLabel?: string;
  size?: ButtonSize;
  selected?: boolean;
  /** Custom label for the Users tab */
  usersTabLabel?: string;
  /** Disable the Teams tab with optional tooltip */
  disableTeamsTab?: boolean;
  teamsTabDisabledTooltip?: string;
}

export interface State {
  groupFilter?: string | null;
  activeTab: Tab;
}

class TabbedUserFilterDropdown extends Component<Props, State> {
  static defaultProps: Props = {
    users: [],
    selectedUsers: [],
    emptyLabel: 'No user',
    position: 'bottom-start',
    teams: [],
    selectedTeams: [],
    unrestrictedWhenEmpty: false,
    singleSelectTeam: false,
    singleSelectUser: false,
    buttonFormType: 'button',
    faIcon: 'user',
    maxHeight: 500,
    minHeight: 400,
    onChange: () => {},
    preventOverflow: true,
    allowFilterGroups: true,
    showUpgradeSubscription: false,
    disabled: false,
  };

  state: State = {
    groupFilter: null,
    activeTab: this.props.selectedTeams.length > 0 ? 'teams' : 'users',
  };

  componentDidUpdate(prevProps: Props) {
    const prevSelectionSize = prevProps.selectedUsers.length + prevProps.selectedTeams.length;
    const newSelectionSize = this.props.selectedUsers.length + this.props.selectedTeams.length;

    // Update activeTab when selection size changes from 0 to positive
    if (prevSelectionSize === 0 && newSelectionSize > 0) {
      this.setState({ activeTab: this.props.selectedTeams.length > 0 ? 'teams' : 'users' });
    }

    // Also update activeTab when selectedTeams changes from empty to non-empty.
    // This workaround is needed because the component is initialized with selectedTeams: [],
    // and doesn't update properly when syncing with URL params
    if (prevProps.selectedTeams.length === 0 && this.props.selectedTeams.length > 0) {
      this.setState({ activeTab: 'teams' });
    }
  }

  handleOnChangeGroup = (groupName: string, ids: Array<number>): void => {
    // Since FilterList unmounts when Modal closes, we'll
    // need to store the active groupFilter here too:
    this.setState({ groupFilter: groupName });
    this.props.onChange(this.state.activeTab, ids);
  };

  // Return a complete list if id's selected on each onChange
  handleOnChange = (user: any, ids: Array<number>): void => {
    this.props.onChange(this.state.activeTab, ids);
  };

  handleBatchSelect = (batchName: string, ids: Array<number>): void => {
    this.props.onChange(this.state.activeTab, ids);
  };

  handleClear = () => {
    this.props.onChange(this.state.activeTab, []);
  };

  handleTabClick = (activeTab: Tab, event: React.SyntheticEvent): void => {
    event.preventDefault();

    // Prevent switching to Teams tab if it's disabled
    if (activeTab === 'teams' && this.props.disableTeamsTab) {
      return;
    }

    // Set tab & reset group filter when switching between users&teams
    this.setState({ activeTab, groupFilter: null });
  };

  renderTabs() {
    // If we don't have any teams, don't render
    if (!this.props.teams.length) {
      return null;
    }

    const { usersTabLabel, disableTeamsTab, teamsTabDisabledTooltip } = this.props;

    const customTabs = [
      {
        value: 'users' as const,
        label: usersTabLabel || 'Users',
      },
      {
        value: 'teams' as const,
        label: 'Teams',
        disabled: disableTeamsTab,
      },
    ];

    const buttonGroup = (
      <ButtonGroup
        stretch
        activeValue={this.state.activeTab}
        options={customTabs}
        onChange={this.handleTabClick}
      />
    );

    return (
      <div className={styles.tabs}>
        {disableTeamsTab && teamsTabDisabledTooltip ? (
          <Tooltip tooltip={teamsTabDisabledTooltip} position="top">
            {buttonGroup}
          </Tooltip>
        ) : (
          buttonGroup
        )}
      </div>
    );
  }

  renderUserList() {
    const options = this.props.users.map(user => userOptionFactory(user));
    const hasDeletedUsers = this.props.users.filter(user => user.deleted).length;
    const canShowFilters = this.props.allowFilterGroups && hasDeletedUsers;
    const groupFilters = canShowFilters
      ? [
          { value: null, name: 'All Users' },
          { value: 'active', name: 'Active' },
          { value: 'deleted', name: 'Deleted' },
        ]
      : false;

    const renderUserOption = (option: Partial<FilterListItem>, meta: OptionMeta) => {
      const showCheckbox = !this.props.singleSelectUser;

      return (
        <div className={classNames('fs-mask', styles.optionRow)}>
          {showCheckbox ? (
            <Checkbox tabIndex={-1} readOnly disabled={meta.isDisabled} checked={meta.isSelected} />
          ) : null}
          {option.image && <img src={option.image} className="fs-exclude" />}
          <DiagnosticResourceId resource="user" id={option.value} />
          <div className={styles.optionLabelWrapper}>
            <Tooltip
              tooltip={option.name || ''}
              usePortal={true}
              position="right"
              delay={250}
              childrenClassName={styles.optionLabel}
            >
              {option.name}
            </Tooltip>
          </div>
          {!!(meta.hint || meta.hintTooltip) && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '5px' }}>
              {meta.hint}
              {meta.hintTooltip}
            </span>
          )}
        </div>
      );
    };

    return (
      <FilterList
        virtualized
        resource="user"
        hideFromFullstory
        maxScrollHeight={this.props.maxHeight}
        minScrollHeight={this.props.minHeight}
        onChange={this.handleOnChange}
        onChangeGroup={this.handleOnChangeGroup}
        onBatchSelect={this.handleBatchSelect}
        values={this.props.selectedUsers}
        options={options}
        placeholder="Search users"
        groupFilter={this.state.groupFilter}
        groupFilters={groupFilters}
        animated={false}
        singleSelect={this.props.singleSelectUser}
        onSearch={this.props.onSearchUsers}
        renderOption={renderUserOption}
      />
    );
  }

  renderTeamList() {
    const hasDeletedTeams = this.props.teams.filter(team => team.deleted).length;
    const groupFilters = hasDeletedTeams
      ? [
          { value: null, name: 'All Teams' },
          { value: 'active', name: 'Active' },
          { value: 'deleted', name: 'Deleted' },
        ]
      : false;

    const renderTeamOption = (option: Partial<FilterListItem>, meta: OptionMeta) => {
      const showCheckbox = !this.props.singleSelectTeam;

      return (
        <div className={classNames('fs-mask', styles.optionRow)}>
          {showCheckbox ? (
            <Checkbox tabIndex={-1} readOnly disabled={meta.isDisabled} checked={meta.isSelected} />
          ) : null}
          {option.image && <img src={option.image} className="fs-exclude" />}
          <DiagnosticResourceId resource="team" id={option.value} />
          <div className={styles.optionLabelWrapper}>
            <Tooltip
              tooltip={option.name || ''}
              usePortal={true}
              position="right"
              delay={250}
              childrenClassName={styles.optionLabel}
            >
              {option.name}
            </Tooltip>
          </div>
          {!!(meta.hint || meta.hintTooltip) && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '5px' }}>
              {meta.hint}
              {meta.hintTooltip}
            </span>
          )}
        </div>
      );
    };

    return (
      <FilterList
        virtualized
        resource="team"
        hideFromFullstory
        maxScrollHeight={this.props.maxHeight}
        minScrollHeight={this.props.minHeight}
        onChange={this.handleOnChange}
        onChangeGroup={this.handleOnChangeGroup}
        onBatchSelect={this.handleBatchSelect}
        values={this.props.selectedTeams}
        options={this.props.teams}
        placeholder="Search teams"
        groupFilter={this.state.groupFilter}
        groupFilters={groupFilters}
        singleSelect={this.props.singleSelectTeam}
        renderOption={renderTeamOption}
      />
    );
  }

  renderUpgradeButton() {
    if (!this.props.showUpgradeSubscription) {
      return null;
    }
    return (
      <UpgradeState messageKey="logForOthersRequiresUpgrade" className={styles.upgradeState} />
    );
  }

  renderTabContent() {
    const { activeTab } = this.state;

    if (!tabs.map(t => t.value).includes(activeTab)) {
      return null;
    }

    if (activeTab === 'users') {
      return this.renderUserList();
    }

    if (activeTab === 'teams') {
      return this.renderTeamList();
    }

    return null;
  }

  renderLabel(): {
    label: React.ReactNode;
    isEmpty: boolean;
    type: string;
    id: null | number;
  } {
    const { compact } = this.props;

    let total;
    let type = '';
    let selected;
    let id: number | null = null;

    if (this.state.activeTab === 'users') {
      total = this.props.users.length;
      selected = this.props.selectedUsers.length;
      // Use singular form for proper pluralization
      type = this.props.usersTabLabel ? 'project assignee' : 'user';
      if (this.props.selectedUsers.length === 1) {
        id = this.props.selectedUsers[0];
      }
    }

    if (this.state.activeTab === 'teams') {
      total = this.props.teams.length;
      selected = this.props.selectedTeams.length;
      type = 'team';
      if (this.props.selectedTeams.length === 1) {
        id = this.props.selectedTeams[0];
      }
    }

    const { unrestrictedWhenEmpty } = this.props;
    const isEmpty = selected === 0;
    const isEmptyAndUnrestricted = unrestrictedWhenEmpty && isEmpty;

    let label = this.props.label
      ? this.props.label
      : isEmptyAndUnrestricted
        ? `Anyone`
        : countableLabel(type, { selected, total, compact });

    // Support "Unassigned"
    if (
      this.state.activeTab === 'users' &&
      this.props.selectedUsers.length === 1 &&
      this.props.selectedUsers[0] === -1
    ) {
      label = this.props.users.find(u => u.id === -1)?.name || label;
    }

    return {
      label,
      isEmpty,
      type,
      id,
    };
  }

  render() {
    const { label, isEmpty, ...resource } = this.renderLabel();
    const { faIcon, buttonFormType, fixed, disabled, unrestrictedWhenEmpty, selected } = this.props;
    const className = classNames(styles.trigger, this.props.buttonClassName);
    const showClearButton = unrestrictedWhenEmpty && (selected ?? !isEmpty);
    const isSelected = this.props.selected ?? !isEmpty;

    return (
      <Popover
        preventOverflow={this.props.preventOverflow}
        position={this.props.position}
        width={350}
        target={
          <ClearableButton
            data-testid="user-filter-dropdown"
            priority="medium"
            selected={isSelected}
            className={className}
            type={buttonFormType as any}
            disabled={disabled}
            onClear={showClearButton ? this.handleClear : undefined}
            isActive={null} // Causes PopoverTarget to control caret
            size={this.props.size}
            clearTooltip={this.props.clearLabel ?? 'Clear the user filter'}
          >
            {!!faIcon && (
              <span>
                <FontAwesome name={faIcon} />
              </span>
            )}
            <DiagnosticResourceId resource={resource.type} id={resource.id} />
            {typeof label === 'string' ? <span className={styles.text}>{label}</span> : label}
          </ClearableButton>
        }
        targetCaret
        customScrollbars
        fixed={fixed}
      >
        {this.renderTabs()}
        <div className={styles.modalContent}>
          {this.renderUpgradeButton()}
          {this.renderTabContent()}
        </div>
      </Popover>
    );
  }
}

export default TabbedUserFilterDropdown;
